import React, { useState, useEffect } from 'react';
import { FileText, Plus, Search, Loader2, Download, Trash2, FileImage, FileVideo, File } from 'lucide-react';
import { documentAPI } from '../api/client';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { toast } from 'sonner';
import ImageUpload from '../components/ImageUpload';

const CATEGORIES = ['photo', 'receipt', 'invoice', 'key_code', 'permit', 'license', 'contract', 'other'];

export default function Documents() {
  const [docs, setDocs] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({ title: '', description: '', category: 'photo', file_type: 'image', file_url: '' });

  useEffect(() => { load(); }, []);
  useEffect(() => {
    let f = docs;
    if (categoryFilter !== 'all') f = f.filter(d => d.category === categoryFilter);
    if (search) f = f.filter(d => d.title?.toLowerCase().includes(search.toLowerCase()));
    setFiltered(f);
  }, [docs, search, categoryFilter]);

  const load = async () => {
    setLoading(true);
    try {
      const data = await documentAPI.list();
      setDocs(data);
    } catch (e) { toast.error('Failed to load'); }
    finally { setLoading(false); }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.file_url) { toast.error('Title and file required'); return; }
    setSubmitting(true);
    try {
      const sizeKb = form.file_url.length * 0.75 / 1024;
      await documentAPI.create({ ...form, file_size_kb: sizeKb });
      toast.success('Document uploaded!');
      setDialogOpen(false);
      setForm({ title: '', description: '', category: 'photo', file_type: 'image', file_url: '' });
      load();
    } catch (e) { toast.error('Failed'); }
    finally { setSubmitting(false); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await documentAPI.delete(id);
      toast.success('Deleted');
      load();
    } catch (e) { toast.error('Failed'); }
  };

  const getIcon = (type) => type === 'image' ? FileImage : type === 'video' ? FileVideo : File;

  return (
    <div className="space-y-6 fade-in" data-testid="documents-page">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-3xl font-bold mb-2 flex items-center gap-2">
            <FileText className="w-8 h-8 text-primary" /> Documents
          </h1>
          <p className="text-muted-foreground">Upload and manage photos, receipts, and files</p>
        </div>
        <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary" data-testid="add-document-button">
          <Plus className="w-4 h-4 mr-2" /> Upload Document
        </Button>
      </div>

      <Card>
        <CardContent className="p-4 flex gap-3 flex-wrap">
          <div className="flex-1 relative min-w-[200px]">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input placeholder="Search documents..." value={search} onChange={(e) => setSearch(e.target.value)} className="pl-10" />
          </div>
          <Select value={categoryFilter} onValueChange={setCategoryFilter}>
            <SelectTrigger className="w-[180px]"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Categories</SelectItem>
              {CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </CardContent>
      </Card>

      {loading ? (
        <Card><CardContent className="p-12 text-center"><Loader2 className="w-8 h-8 animate-spin mx-auto" /></CardContent></Card>
      ) : filtered.length === 0 ? (
        <Card><CardContent className="p-12 text-center">
          <FileText className="w-16 h-16 mx-auto text-muted-foreground mb-3" />
          <p className="text-muted-foreground mb-4">No documents yet</p>
          <Button onClick={() => setDialogOpen(true)} className="btn-gradient-primary">
            <Plus className="w-4 h-4 mr-2" /> Upload First Document
          </Button>
        </CardContent></Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {filtered.map(d => {
            const Icon = getIcon(d.file_type);
            const isImage = d.file_type === 'image' || d.file_url?.startsWith('data:image');
            return (
              <Card key={d.id} className="card-hover overflow-hidden">
                <div className="h-40 bg-muted/30 flex items-center justify-center">
                  {isImage ? (
                    <img src={d.file_url} alt={d.title} className="w-full h-full object-cover" />
                  ) : (
                    <Icon className="w-16 h-16 text-muted-foreground" />
                  )}
                </div>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between mb-2">
                    <h3 className="font-semibold flex-1 truncate">{d.title}</h3>
                    <Badge variant="outline" className="capitalize ml-2">{d.category}</Badge>
                  </div>
                  {d.description && <p className="text-xs text-muted-foreground mb-2 line-clamp-2">{d.description}</p>}
                  <div className="flex gap-1 mt-3">
                    <a href={d.file_url} download={d.title} className="flex-1">
                      <Button size="sm" variant="outline" className="w-full"><Download className="w-3 h-3 mr-1" />Download</Button>
                    </a>
                    <Button size="sm" variant="outline" onClick={() => handleDelete(d.id)} className="text-destructive">
                      <Trash2 className="w-3 h-3" />
                    </Button>
                  </div>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent>
          <DialogHeader><DialogTitle>Upload Document</DialogTitle><DialogDescription>Upload a photo, receipt, or file</DialogDescription></DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-3">
            <div className="space-y-2"><Label>Title *</Label><Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required /></div>
            <div className="space-y-2"><Label>Category</Label>
              <Select value={form.category} onValueChange={(v) => setForm({ ...form, category: v })}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>{CATEGORIES.map(c => <SelectItem key={c} value={c} className="capitalize">{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div className="space-y-2"><Label>Description</Label><Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} /></div>
            <div className="space-y-2"><Label>File *</Label>
              <ImageUpload value={form.file_url} onChange={(url) => setForm({ ...form, file_url: url, file_type: 'image' })} label="Upload Image/File" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" className="btn-gradient-primary" disabled={submitting}>{submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Upload'}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
